import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material"
import { ReconcileIconButton } from "./styles"
import { useEffect, useState } from "react"
import {
  resetTransactionActionSuccess,
  selectReconciliation,
} from "../../slices/reconcilationSlice"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  selectTransactions,
  getProcessSchedule,
  getTransactionList,
} from "../../slices/transactionsSlice"
import { constructApiRequestParams } from "../../utils/transactionHelpers"
import { allowedOnlyBankAccountsRoles } from "../../constants/data"
import { selectLogin } from "../../slices/loginSlice"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { Link } from "react-router-dom"
import { formatPriceWithRupeeSymbol } from "../../utils/price-formatter"
import { Invoice } from "../../models/transactions.model"

type TransactionActionProps = Readonly<{
  id: string
  title: string
  confirmText: string
  handleTransactionRequest: any
  invoices?: Invoice[]
}>

function TransactionAction({
  title,
  confirmText,
  handleTransactionRequest,
  invoices,
}: TransactionActionProps) {
  const { loading, transactionActionSuccess } =
    useAppSelector(selectReconciliation)
  const { role } = useAppSelector(selectLogin)
  const { filters, pagination, processScheduleData } =
    useAppSelector(selectTransactions)
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)

  const handleRefresh = () => {
    dispatch(getTransactionList(constructApiRequestParams(filters, pagination)))
    if (allowedOnlyBankAccountsRoles.includes(role)) {
      dispatch(getProcessSchedule())
    }
  }

  useEffect(() => {
    if (transactionActionSuccess) {
      handleRefresh()
      dispatch(resetTransactionActionSuccess())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionActionSuccess])

  const totalAmount =
    invoices?.reduce(
      (sum: number, inv: any) => sum + parseFloat(inv.amount || 0),
      0,
    ) || 0
  const totalTax =
    invoices?.reduce(
      (sum: number, inv: any) => sum + parseFloat(inv.tax_amount_withheld || 0),
      0,
    ) || 0

  const rows: GridRowsProp = Array.isArray(invoices)
    ? [
        ...invoices.map((inv, index) => ({
          id: inv.invoiceId || index,
          invoiceNumber: inv.invoiceNumber,
          invoiceURL: inv.invoiceURL,
          amount: formatPriceWithRupeeSymbol(inv.amount),
          tax_amount_withheld: formatPriceWithRupeeSymbol(
            inv.taxAmountWithheld,
          ),
          isSummary: false,
        })),
        {
          id: "summary-row",
          invoiceNumber: "Total",
          amount: formatPriceWithRupeeSymbol(totalAmount),
          tax_amount_withheld: formatPriceWithRupeeSymbol(totalTax),
          isSummary: true,
        },
      ]
    : []

  const columns: GridColDef[] = [
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      width: 120,
      flex: 0.7,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isSummary) {
          return (
            <Typography style={{ fontWeight: 700 }}>{params.value}</Typography>
          )
        }

        return (
          <Link
            to={params.row.invoiceURL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {params.value}
          </Link>
        )
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      type: "number",
      flex: 0.7,
      sortable: false,
    },
    {
      field: "tax_amount_withheld",
      headerName: "Tax Withheld",
      type: "number",
      width: 90,
      flex: 0.7,
      sortable: false,
    },
  ]

  return (
    <>
      <ReconcileIconButton
        disabled={processScheduleData.status}
        onClick={() => {
          setOpen(true)
        }}
      >
        {title}
      </ReconcileIconButton>
      <Dialog
        open={open}
        maxWidth="xs"
        aria-labelledby="update-dialog-title"
        aria-describedby="update-dialog-description"
      >
        <DialogTitle id="update-dialog-title">{confirmText}</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            flexDirection: "column",
          }}
        >
          {invoices && invoices.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This transaction contains {invoices.length} invoice
                {invoices.length > 1 ? "s" : ""}:
              </Typography>

              <Box sx={{ mb: 3 }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  hideFooter={true}
                  disableColumnMenu={true}
                  isRowSelectable={(params) => !params.row.isSummary}
                  getRowClassName={(params) =>
                    params.row.isSummary ? "summary-row" : ""
                  }
                  sx={{
                    "& .summary-row": {
                      fontWeight: 700,
                    },
                  }}
                />
              </Box>
            </>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={loading}
              onClick={() => {
                if (!loading) {
                  handleTransactionRequest()
                }
                setOpen(false)
              }}
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default TransactionAction
